import React from "react";
import { Navigate, Outlet, Route, useLocation, useParams } from "react-router-dom";
import { AuthorizedRoute } from "../shared/components/Routers";
import { Roles } from "../account/models/AccountModels";
import TypeGuard from "../shared/components/TypeGuard";
import PermissionRouteGuard from "../shared/components/PermissionRouteGuard";
import ViewerRouteGuard from "../shared/components/ViewerRouteGuard";
import { Modules } from "../core/models/PermissionModels";
import accountContext from "../account/accountContext";
import Search from "./searchAndBuy/components/Search";
import ProductSuppliers from "./productSuppliers/components/ProductSuppliers";
import Orders from "./orders/components/Orders";
import InventoryTable from "./inventory/components/InventoryTable";
import UploadStockItemsFile from "./inventory/components/UploadStockItemsFile";
import EditStockItem from "./inventory/components/EditStockItem";
import MyRfqs from "./myRfqs/components/MyRfqs";
import CreateRfq from "./myRfqs/components/create/CreateRfq";
import RfqFeed from "./rfqFeed/components/RfqFeed";
import CreateOffer from "./sentOffers/components/create/CreateOffer";
import SentOffers from "./sentOffers/components/SentOffers";
import OffersReceived from "./offersReceived/components/OffersReceived";
// SETTINGS_V2_SP10 flip: legacy MUI settings imports (SettingsLayout, Settings*
// sections, SettingsAppearance) removed — the legacy tree is retired (Stage 1;
// components stay compiled but unrouted, rollback = re-add). v2 is now default.
import SettingsV2Layout from "./settings/v2/SettingsV2Layout";
import ProfilePageV2 from "./settings/v2/pages/ProfilePageV2";
import CompanyPageV2 from "./settings/v2/pages/CompanyPageV2";
import LocationsPageV2 from "./settings/v2/pages/LocationsPageV2";
import NameplatesPageV2 from "./settings/v2/pages/NameplatesPageV2";
import NotificationsPageV2 from "./settings/v2/pages/NotificationsPageV2";
import TeamPageV2 from "./settings/v2/pages/TeamPageV2";
import PricingPageV2 from "./settings/v2/pages/PricingPageV2";
import ServicePageV2 from "./settings/v2/pages/ServicePageV2";
import ShippingPageV2 from "./settings/v2/pages/ShippingPageV2";
import Companies from "./companies/components/Companies";
import Company from "./companies/components/Company";
import Machinery from "./machinery/components/Machinery";
import Machine from "./machinery/components/Machine";
import CatalogBrowsePage from "./machinery/components/CatalogBrowsePage";
import CatalogModelDetailPage from "./machinery/components/CatalogModelDetailPage";
import SrDetailPage from "./machinery/components/srDetail/SrDetailPage";
import CreateServiceReportPage from "./machinery/components/CreateServiceReportPage";
import SolveServiceReportPage from "./machinery/components/SolveServiceReportPage";
// MACHINERY V2 (M2.0) — parallel build surface, static (non-lazy) top-level
// import per ticket §3. Mirrors the MIDAS v3 coexistence precedent.
import MachineryV2Layout from "./machinery/v2/MachineryV2Layout";
import FleetPage from "./machinery/v2/fleet/FleetPage";
import MachineProfileV2 from "./machinery/v2/profile/MachineProfileV2";
// SETTINGS_V2_SP10 flip: legacy `LocationsPage` (machinery/components/locations)
// removed — its ONLY route was legacy `settings/locations` (now retired);
// v2 uses LocationsPageV2. The machinery module still exports it for its own use.
import MyServiceRequests from "./servicing/components/MyServiceRequests";
import ViewServiceRequestPage from "./servicing/components/ViewServiceRequestPage";
import RfsDetailPage from "./servicing/components/rfsDetail/RfsDetailPage";
import EditServiceRequestPage from "./servicing/components/EditServiceRequestPage";
import CompareOffersPage from "./servicing/components/CompareOffersPage";
import ServiceBoard from "./serviceBoard/components/ServiceBoard";
import MyOffers from "./serviceOffers/components/MyOffers";
import ActiveWork from "./serviceOffers/components/ActiveWork";
import OfferDetailPage from "./serviceOffers/components/offerDetail/OfferDetailPage";
import EditOfferPage from "./serviceOffers/components/EditOfferPage";
import NotificationHistoryPage from "../shared/notifications/components/NotificationHistoryPage";
import PartnershipsPage from "./partnerships/components/PartnershipsPage";
import ServiceTrackingPage from "./serviceTracking/components/ServiceTrackingPage";
import MidasPage from "./midas/components/MidasPage";
import MidasRouteGuard from "./midas/components/MidasRouteGuard";
import MidasPageV2 from "./midas/components/v2/MidasPageV2";
import MidasV2Layout from "./midas/components/v2/MidasV2Layout";
import MidasHistoryPageV2 from "./midas/components/v2/history/MidasHistoryPageV2";
import MidasPageV3 from "./midas/components/v3/MidasPageV3";
import MidasV3Layout from "./midas/components/v3/MidasV3Layout";
import MidasHistoryPageV3 from "./midas/components/v3/history/MidasHistoryPageV3";
// ME-MACHINEID-RENDER SP-6: dev-only, UNLINKED, data-free fixture harness — the
// pre-flip visual-verification surface for the MASH evidence viewers. NOT in nav.
import MachineEvidenceFixtureHarnessV3 from "./midas/components/v3/artifacts/dev/MachineEvidenceFixtureHarnessV3";
import EdgeLiveMonitorPage from "../internal/edgeMonitor/components/EdgeLiveMonitorPage";
import ServiceReportsPage from "./operations/components/serviceReports/ServiceReportsPage";
import WorkBoardPage from "./operations/components/workBoard/WorkBoardPage";
import MyTasksPage from "./operations/components/myTasks/MyTasksPage";
import CreateWorkOrderPage from "./operations/components/CreateWorkOrderPage";
import WorkOrderDetailPage from "./operations/components/WorkOrderDetailPage";
import AnalyticsDashboardPage from "./operations/components/analytics/AnalyticsDashboardPage";
import useDefaultRedirect from "../account/hooks/useDefaultRedirect";

/** Guard that blocks Workers from admin Operations sub-sections. Redirects to /operations/my-tasks. */
const OpsManagerGuard = () => {
  const user = accountContext.user;
  const isWorker = user?.role === Roles.WORKER;
  if (isWorker) return <Navigate to="/operations/my-tasks" replace />;
  return <Outlet />;
};

/** Guard that blocks Workers from catalog routes. Redirects to /machinery. */
const MachineryManagerGuard = () => {
  const user = accountContext.user;
  const isWorker = user?.role === Roles.WORKER;
  if (isWorker) return <Navigate to="/machinery" replace />;
  return <Outlet />;
};

/** Conditional redirect: Admin/Manager → work-board, Worker → my-tasks */
const OpsIndexRedirect = () => {
  const user = accountContext.user;
  const isWorker = user?.role === Roles.WORKER;
  return <Navigate to={isWorker ? "my-tasks" : "work-board"} replace />;
};

/** Redirect /service-requests/:id → /service-board/:id */
const ServiceRequestRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/service-board/${id}`} replace />;
};

/** Redirect /service-requests/:id/offers/compare → /service-board/:id/offers/compare */
const ServiceRequestCompareRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/service-board/${id}/offers/compare`} replace />;
};

/** SETTINGS_V2_SP10 — permanent alias: /settings-v2[/*] → /settings[/*], preserving
 *  sub-path, query and hash. The `i` flag is REQUIRED (SEC-REQ-1): React Router
 *  matches paths case-insensitively, so a case-variant URL (e.g. /Settings-V2)
 *  reaches this component — without `i` the regex would not match, the target
 *  would stay /Settings-V2/…, re-match the alias route, and infinite-loop. */
const SettingsV2AliasRedirect = () => {
  const { pathname, search, hash } = useLocation();
  const target =
    pathname.replace(/^\/settings-v2(?=\/|$)/i, "/settings") + search + hash;
  return <Navigate to={target} replace />;
};

const useCustomerRoutes = () => {
  const { defaultRoute } = useDefaultRedirect();

  return (
    <Route path="">
      {/* MidasPilot confinement is NOT mounted here — PilotRouteGuard wraps the `/` Layout
          route's children in App.tsx instead (MIDAS_PILOT_ROLE_SPEC.md §5.2, mount point
          AMENDED 2026-08-18), so it also covers the unguarded `tech` sibling tree. Every
          route below is already inside it; do not re-mount it here (double redirect). */}
      {/* Viewer role: no web app access — redirect to QR kiosk */}
      <Route element={<ViewerRouteGuard />}>
      {/* Operator-only routes */}
      <Route element={<TypeGuard requiredType="operator" />}>
        <Route element={<PermissionRouteGuard module={Modules.Machinery} />}>
          <Route path="machinery" element={<AuthorizedRoute role={Roles.ALL_COMPANY_ROLES} />}>
            <Route element={<MachineryManagerGuard />}>
              <Route path="catalog" element={<CatalogBrowsePage />} />
              <Route path="catalog/:modelId" element={<CatalogModelDetailPage />} />
            </Route>
            <Route path=":id/service-reports/new" element={<CreateServiceReportPage />}></Route>
            <Route path=":id/service-reports/:reportNumber/solve" element={<SolveServiceReportPage />}></Route>
            <Route path=":id/service-reports/:reportNumber" element={<SrDetailPage />}></Route>
            <Route path=":id" element={<Machine />}></Route>
            <Route path="" element={<Machinery />}></Route>
          </Route>
        </Route>

        {/* MACHINERY V2 (Stroje redesign) — PARALLEL surface, M2.0 foundation.
            MACHINERY_V2_IMPLEMENTATION_TICKET.md §2.1. The live `machinery`
            block above stays byte-unchanged until cutover (M2.12); this block
            is purely additive and shares the SAME guard chain:
              TypeGuard(operator) [inherited from the wrapper above]
                → PermissionRouteGuard(Machinery)
                → AuthorizedRoute(ALL_COMPANY_ROLES)
                → MachineryV2Layout (scoped root + token sheet + tooltip provider)
            so the v2 routes introduce NO new authorization surface.

            Children arrive with their sub-phases and mount inside the layout:
              M2.2 + M2.3 → `""` fleet — DONE (FleetPage: shell + V1/V2 bodies).
              M2.4 → `:id`     profile v2 — DONE (MachineProfileV2: hero +
                     pill tabs; tab BODIES are M2.5 / M2.10 placeholders).
              M2.6 → `:id/service-reports/new` + `:id/service-reports/:reportNumber`
              M2.8 → `catalog` + `catalog/:modelId`, wrapped in
                     <MachineryManagerGuard /> exactly like the live block
                     (Workers are blocked from catalog on BOTH surfaces).
            The catalog wrapper is not mounted yet because its v2 components do
            not exist — an empty guard route would never match (react-router
            skips pathless, child-less routes) and would be dead code. */}
        <Route element={<PermissionRouteGuard module={Modules.Machinery} />}>
          <Route path="machinery-v2" element={<AuthorizedRoute role={Roles.ALL_COMPANY_ROLES} />}>
            <Route element={<MachineryV2Layout />}>
              <Route path=":id" element={<MachineProfileV2 />}></Route>
              <Route path="" element={<FleetPage />}></Route>
            </Route>
          </Route>
        </Route>

        {/* Operations routes */}
        <Route element={<PermissionRouteGuard module={Modules.Maintenance} />}>
          <Route path="operations" element={<AuthorizedRoute role={Roles.ALL_COMPANY_ROLES} />}>
            <Route index element={<OpsIndexRedirect />} />
            {/* Manager+ sub-sections (Workers redirect to my-tasks) */}
            <Route element={<OpsManagerGuard />}>
              <Route path="service-reports" element={<ServiceReportsPage />} />
              <Route path="work-board" element={<WorkBoardPage />} />
              <Route path="schedule" element={<Navigate to="/operations/work-board?view=calendar" replace />} />
              <Route path="analytics" element={<AnalyticsDashboardPage />} />
              <Route path="work-orders/new" element={<CreateWorkOrderPage />} />
            </Route>
            {/* Available to all (including Workers) */}
            <Route path="my-tasks" element={<MyTasksPage />} />
            <Route path="work-orders/:id" element={<WorkOrderDetailPage />} />
          </Route>
        </Route>

        <Route path="search/:productId/:productNumber" element={<ProductSuppliers />}></Route>
        <Route path="search" element={<Search />}></Route>

        <Route element={<PermissionRouteGuard module={Modules.Trading} />}>
          <Route path="trading" element={<AuthorizedRoute role={Roles.ALL_COMPANY_ROLES} />}>
            <Route path="my-rfqs" element={<MyRfqs />}></Route>
            <Route path="my-rfqs/new" element={<CreateRfq />}></Route>
            <Route path="rfq-feed" element={<RfqFeed />}></Route>
            <Route path="new-offer/:id" element={<CreateOffer />}></Route>
            <Route path="offers-received" element={<OffersReceived />}></Route>
            <Route path="offers-sent" element={<SentOffers />}></Route>
            <Route path="orders" element={<Orders />}></Route>
            <Route path="" element={<Navigate to="my-rfqs" />}></Route>
          </Route>
        </Route>

        <Route element={<PermissionRouteGuard module={Modules.Inventory} />}>
          <Route path="supplier" element={<AuthorizedRoute role={Roles.ALL_COMPANY_ROLES} />}>
            <Route path="inventory" element={<InventoryTable />}></Route>
            <Route path="inventory/:id" element={<Navigate to="/supplier/inventory" replace />}></Route>
            <Route path="upload/:id" element={<UploadStockItemsFile />}></Route>
            <Route path="new/:groupId" element={<EditStockItem />}></Route>
            <Route path=":id" element={<EditStockItem />}></Route>
            <Route path="" element={<Navigate to="inventory" />}></Route>
          </Route>
        </Route>

        <Route element={<PermissionRouteGuard module={Modules.Servicing} />}>
          <Route path="service-requests" element={<AuthorizedRoute role={Roles.ALL_COMPANY_ROLES} />}>
            <Route path="" element={<MyServiceRequests />}></Route>
            <Route path="new" element={<EditServiceRequestPage />}></Route>
            <Route path="edit/:id" element={<EditServiceRequestPage />}></Route>
            <Route path=":id/offers/compare" element={<ServiceRequestCompareRedirect />}></Route>
            <Route path=":id" element={<ServiceRequestRedirect />}></Route>
          </Route>
        </Route>
      </Route>

      {/* Provider-only routes */}
      <Route element={<TypeGuard requiredType="provider" />}>
        <Route element={<PermissionRouteGuard module={Modules.Servicing} />}>
          <Route path="service-offers" element={<AuthorizedRoute role={Roles.ALL_COMPANY_ROLES} />}>
            <Route path="" element={<MyOffers />}></Route>
            <Route path="new/:serviceRequestId" element={<EditOfferPage />}></Route>
            <Route path="edit/:id" element={<EditOfferPage />}></Route>
          </Route>
          <Route path="active-work" element={<AuthorizedRoute role={Roles.ALL_COMPANY_ROLES} />}>
            <Route path="" element={<ActiveWork />}></Route>
          </Route>
        </Route>
      </Route>

      {/* Visible to all types — offer detail accessible by both owner and provider */}
      <Route element={<PermissionRouteGuard module={Modules.Servicing} />}>
        <Route path="service-offers/:id" element={<AuthorizedRoute role={Roles.ALL_COMPANY_ROLES} />}>
          <Route path="" element={<OfferDetailPage />}></Route>
        </Route>
      </Route>

      {/* Visible to all types */}
      <Route element={<PermissionRouteGuard module={Modules.Servicing} />}>
        <Route path="service-board" element={<AuthorizedRoute role={Roles.ALL_COMPANY_ROLES} />}>
          <Route path="" element={<ServiceBoard />}></Route>
          <Route path=":id/offers/compare" element={<CompareOffersPage />}></Route>
          <Route path=":id" element={<RfsDetailPage />}></Route>
        </Route>
      </Route>

      <Route element={<PermissionRouteGuard module={Modules.Servicing} />}>
        <Route path="service-tracking" element={<AuthorizedRoute role={Roles.ALL_COMPANY_ROLES} />}>
          <Route path="" element={<ServiceTrackingPage />}></Route>
        </Route>
      </Route>

      <Route element={<PermissionRouteGuard module={Modules.PartnerHub} />}>
        <Route path="companies" element={<Companies />}></Route>
        <Route path="companies/:id" element={<Company />}></Route>
        <Route path="partnerships" element={<AuthorizedRoute role={Roles.ALL_COMPANY_ROLES} />}>
          <Route path="" element={<PartnershipsPage />}></Route>
        </Route>
      </Route>

      {/* MIDAS full-screen assistant (default = V3) -- AuthorizedRoute + isBotEnabled gate (SEC-REQ-2).
          V3 is now the default and renders at /midas (sub-routes: /midas/history).
          V1 (MidasPage) moved to /midas-v1. The old /midas-v3 route is retired
          and intentionally has no redirect — unknown paths fall through to the
          app's normal not-found behaviour. */}
      <Route path="midas" element={<AuthorizedRoute role={Roles.ALL_COMPANY_ROLES} />}>
        <Route element={<MidasRouteGuard />}>
          <Route element={<MidasV3Layout />}>
            <Route path="" element={<MidasPageV3 />} />
            <Route path="history" element={<MidasHistoryPageV3 />} />
          </Route>
        </Route>
      </Route>

      {/* ME-MACHINEID-RENDER SP-6 — dev-only fixture harness. UNLINKED (no nav),
          DATA-FREE (renders only imported static fixtures, ZERO API calls, nothing
          read from the URL/tenant). Mounted as its OWN route (NOT under the MIDAS
          layout/guard, which would pull chat/session state) so it stays isolated.
          Behind base SPA auth only. React Router ranks this 2-segment path above
          the `midas` parent for `/midas/_fixtures`. Safe to ship unlinked in the
          bundle — it exposes nothing. */}
      <Route path="midas/_fixtures" element={<AuthorizedRoute role={Roles.ALL_COMPANY_ROLES} />}>
        <Route path="" element={<MachineEvidenceFixtureHarnessV3 />} />
      </Route>

      {/* MIDAS V1 (legacy default, moved here from /midas).
          Same auth chain: AuthorizedRoute + MidasRouteGuard (SEC-REQ-2). */}
      <Route path="midas-v1" element={<AuthorizedRoute role={Roles.ALL_COMPANY_ROLES} />}>
        <Route element={<MidasRouteGuard />}>
          <Route path="" element={<MidasPage />} />
        </Route>
      </Route>

      {/* MIDAS V2 (Phase 0 — infrastructure-only placeholder).
          Same auth chain as /midas: AuthorizedRoute + MidasRouteGuard preserves SEC-REQ-1.
          Phase 4 adds /midas-v2/history full archive page under the same guard.
          Phase 4.1: both sub-routes nest under MidasV2Layout so they share the
          V2 sidebar shell (260/280px sidebar + Mode card + Fleet/History tabs +
          thread groups). The layout component wires sidebar UI state, breakpoint
          overlays, and an Outlet for the active sub-page. The platform's existing
          global MachineID app top bar continues to render above this layout
          unchanged — see PHASE4_HISTORY_FIX_SPEC.md §6 D1. */}
      <Route path="midas-v2" element={<AuthorizedRoute role={Roles.ALL_COMPANY_ROLES} />}>
        <Route element={<MidasRouteGuard />}>
          <Route element={<MidasV2Layout />}>
            <Route path="" element={<MidasPageV2 />} />
            <Route path="history" element={<MidasHistoryPageV2 />} />
          </Route>
        </Route>
      </Route>

      {/* Internal staff tooling — no role gate v1 per EDGE_LIVE_MONITOR_SPEC.md §6.2.
          Base SPA auth (AuthorizedRoute with no role) is the only check.
          Pre-`.com` cutover MUST add a staff/IsMachineIdStaff guard per §16. */}
      <Route path="internal/tools/edge-monitor" element={<AuthorizedRoute />}>
        <Route path="" element={<EdgeLiveMonitorPage />} />
      </Route>

      {/* SETTINGS_V2_SP10 flip — Settings v2 is now the DEFAULT settings surface.
          The v2 pages render at /settings/*; the legacy MUI tree is RETIRED (Stage 1
          — its components stay compiled but unrouted, so rollback = re-add routes).
          Guard nesting is byte-equivalent to the pre-flip trees: global
          ViewerRouteGuard > PermissionRouteGuard(Settings, default level) > layout.
          APPEARANCE is fully retired (Branch B) — no route anywhere; old
          /settings/appearance bookmarks fall through to the app's `*` catch-all →
          default redirect (same as any retired path). fontTheme/accentTheme
          persistence + bootstrap are untouched (they live outside Settings). */}
      {/* allowMidasPilot: the pilot is seeded Settings:0 but the owner requires the
          Settings surface reachable (TAJMAC-PILOT-FE3) — sections render grayed via
          useSettingsV2Nav, and PilotRouteGuard still admits only /settings +
          /settings/profile. Route access only; the zeroed API permissions stand. */}
      <Route element={<PermissionRouteGuard module={Modules.Settings} allowMidasPilot />}>
        <Route path="settings" element={<SettingsV2Layout />}>
          <Route index element={<Navigate to="profile" replace />} />
          <Route path="profile" element={<ProfilePageV2 />} />
          <Route path="notifications" element={<NotificationsPageV2 />} />
          <Route path="company" element={<CompanyPageV2 />} />
          <Route path="locations" element={<LocationsPageV2 />} />
          {/* B7-FE1 (plan §6A.2) — same page twice on purpose: the bare path is
              the fleet table, the :machineId path auto-scrolls/highlights/opens
              that row's detail. The machine QR-dialog cog lands on the second
              form so the owner arrives on the machine they came from. */}
          <Route path="nameplates" element={<NameplatesPageV2 />} />
          <Route path="nameplates/:machineId" element={<NameplatesPageV2 />} />
          <Route path="team" element={<TeamPageV2 />} />
          <Route path="pricing" element={<PricingPageV2 />} />
          <Route path="service" element={<ServicePageV2 />} />
          <Route path="shipping" element={<ShippingPageV2 />} />
        </Route>
        <Route path="settings/my-account" element={<Navigate to="/settings/profile" replace />} />

        {/* PERMANENT alias — /settings-v2 bookmarks + old in-app links survive
            (user gate 2026-08-05). Kept indefinitely; a future removal is its own
            human decision. Sits INSIDE the same PermissionRouteGuard so
            /settings-v2/* keeps today's exact guard behavior for non-admin roles
            (the guard fallback fires before any redirect — no probe-able diff). */}
        <Route path="settings-v2" element={<SettingsV2AliasRedirect />} />
        <Route path="settings-v2/*" element={<SettingsV2AliasRedirect />} />
      </Route>

      <Route element={<PermissionRouteGuard module={Modules.Notifications} />}>
        <Route path="notifications" element={<AuthorizedRoute role={Roles.ALL_COMPANY_ROLES} />}>
          <Route path="" element={<NotificationHistoryPage />} />
        </Route>
      </Route>

      </Route>{/* end ViewerRouteGuard */}

      <Route path="" element={<Navigate to={defaultRoute()} />} />
      <Route path="*" element={<Navigate to="" />} />
    </Route>
  );
};

export default useCustomerRoutes;
